import React, { useState } from 'react';
import { Copy, Check } from '@phosphor-icons/react';
import DocumentsModal from './DocumentsModal';

/**
 * Modal para mostrar la dirección del cliente cuando el técnico está en camino
 * @param {Object} task - Tarea con información del cliente
 * @param {boolean} isOpen - Si el modal está abierto
 * @param {Function} onClose - Callback para cerrar el modal
 * @param {Function} onOpenGoogleMaps - Callback para abrir Google Maps
 */
function AddressModal({ task, isOpen, onClose, onOpenGoogleMaps }) {
    if (!isOpen || !task) return null;
    const [copied, setCopied] = useState(false);
    const [copiedSecondary, setCopiedSecondary] = useState(false);
    const [docsOpen, setDocsOpen] = useState(false);

    // Función para copiar el número de teléfono al portapapeles
    const handleCopyPhone = async (phoneNumber, isSecondary = false) => {
        if (!phoneNumber) return;

        let success = false;

        // Método 1: Clipboard API moderno (HTTPS/localhost)
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(phoneNumber);
                success = true;
            } catch (err) {
                console.warn('Clipboard API failed, trying fallback:', err);
            }
        }

        // Método 2: Fallback usando textarea + execCommand (HTTP)
        if (!success) {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = phoneNumber;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);

                textarea.select();
                textarea.setSelectionRange(0, phoneNumber.length);

                success = document.execCommand('copy');
                document.body.removeChild(textarea);
            } catch (err) {
                console.error('Fallback copy failed:', err);
            }
        }

        if (success) {
            if (isSecondary) {
                setCopiedSecondary(true);
                setTimeout(() => setCopiedSecondary(false), 2000);
            } else {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        }
    };

    return (
        <>
            {!docsOpen && (
                <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[9999] p-4">
                    <div data-tour="tech-details" className="bg-surface rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <h3 className="text-lg font-semibold text-content">
                                    Detalles de la tarea
                                </h3>
                            </div>

                            <div className="mb-4">
                                <h4 className="text-sm font-bold text-content-muted mb-2">Título:</h4>
                                <p className="text-content font-medium text-sm">
                                    {task.title || 'Titulo no disponible'}
                                </p>
                            </div>


                            <div>
                                {task.client?.phone && (
                                    <div className="mb-4">
                                        <h4 className="text-sm font-bold text-content-muted mb-2">Teléfono:</h4>
                                        <div className="flex items-center gap-2">
                                            <p className="text-content font-medium text-sm">{task.client?.phone}</p>
                                            {copied ? (
                                                <Check size={20} className="text-success" />
                                            ) : (
                                                <Copy size={20} className="text-content-muted cursor-pointer hover:text-content transition-colors" onClick={() => handleCopyPhone(task.client?.phone)} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                {task.client?.secondaryPhone && (
                                    <div className="mb-4">
                                        <h4 className="text-sm font-bold text-content-muted mb-2">Teléfono secundario:</h4>
                                        <div className="flex items-center gap-2">
                                            <p className="text-content font-medium text-sm">{task.client?.secondaryPhone}</p>
                                            {copiedSecondary ? (
                                                <Check size={20} className="text-success" />
                                            ) : (
                                                <Copy size={20} className="text-content-muted cursor-pointer hover:text-content transition-colors" onClick={() => handleCopyPhone(task.client?.secondaryPhone, true)} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mb-4">
                                <h4 className="text-sm font-bold text-content-muted mb-2">Descripción:</h4>
                                <p className="text-content font-medium text-sm whitespace-pre-wrap">
                                    {task.description || 'Descripción no disponible'}
                                </p>
                            </div>

                            {task.status !== 'pendiente' && <div className="mb-4">
                                <h4 className="text-sm font-bold text-content-muted mb-2">Dirección:</h4>
                                <p className="text-content font-medium text-sm">
                                    {task.client?.address || 'Dirección no disponible'}
                                </p>
                            </div>}
                            {(true || (task.documents && task.documents.length > 0)) && <div className="mb-4">
                                <button
                                    data-tour="tech-details-documents"
                                    onClick={() => setDocsOpen(true)}
                                    className="w-full bg-surface-2 hover:bg-line text-content px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                                >
                                    Documentos
                                </button>
                            </div>}
                            <div className="flex justify-between">
                                {task.status !== 'pendiente' && <div>
                                    <button
                                        onClick={() => onOpenGoogleMaps(task)}
                                        className="bg-amber hover:bg-amber-hover text-on-amber px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2"
                                    >
                                        <span>Google Maps</span>
                                    </button>
                                </div>}

                                <div>
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 bg-surface-2 hover:bg-line text-content rounded-lg transition-colors"
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <DocumentsModal task={task} isOpen={docsOpen} onClose={() => setDocsOpen(false)} />
        </>
    );
}

export default AddressModal;
